import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import { MatDialogRef } from '@angular/material/dialog'
import {
  addRentalComplete,
  addSubUnitRentals,
  addSubUnitRentalsComplete,
  ConfirmDialogService,
  ConfirmDialogStatus,
  convertToTokeetEmail,
  CreateRentalPayload,
  integer,
  notAllSpacesValidator,
  Rental,
  rentalEmailPrefixFromName,
  RentalEmailValidator,
  SaveForm,
  selectRentalsWithoutGroupItems,
  setRentalSubUnitsComplete,
  Toaster,
} from '@tokeet-frontend/tv3-platform'
import { countries } from '@tokeet-frontend/tv3-platform'
import * as R from 'ramda'
import * as lodash from 'lodash'
import { addRental, selectSubdomain } from '@tokeet-frontend/tv3-platform'
import { RentalEmailNotTakenValidator } from '@tokeet-frontend/tv3-platform'
import { RentalNameNotTakenValidator } from '@tokeet-frontend/tv3-platform'
import { notAllNumbersValidator } from '@tokeet-frontend/tv3-platform'
import { ParsedGooglePlace } from '@tokeet-frontend/tv3-platform'
import { Router } from '@angular/router'
import { AccountGuard, SubdomainGuard } from '@tokeet-frontend/tv3-platform'
import { DataCheckerService } from '@tokeet-frontend/tv3-platform'
import { Destroyable, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import { selectActiveTokeetPlanView } from '@tv3/store/plan/plan.selectors'
import {
  UserStorage,
  setRentalSubUnits,
  createRentalPayloadFromMainUnit,
  defaultRentalColor,
} from '@tokeet-frontend/tv3-platform'
import { of } from 'rxjs'
import { map, switchMap, take, tap } from 'rxjs/operators'
import { Actions, ofType } from '@ngrx/effects'
import { createMultiUnitSettingsForm } from '../multi-unit-reservation-settings/multi-unit-reservation-settings.component'
import { MatStepper } from '@angular/material/stepper'
import { RentalGroupSuccessDialogService } from '../rental-group-success-dialog/rental-group-success-dialog.service'
import { OpenRentalOverlay } from '@tv3/store/overlay/overlay.actions'
import { RentalDialogTab } from '../../overlays/rental-overlay/rental-overlay.component'
import { selectLinkedRentals } from '@tv3/store/connection/connection.selectors'
import { isTokeetStandaloneVersion } from '@tv3/store/plan/plan.view'

@Component({
  selector: 'app-add-multi-unit-dialog',
  templateUrl: './add-multi-unit-dialog.component.html',
  styleUrls: ['./add-multi-unit-dialog.component.scss'],
  host: {
    class: 'modal-content',
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AddMultiUnitDialogComponent extends Destroyable implements OnInit {
  countryList = countries

  tab: 'create' | 'connect' = 'create'

  unitForm = this.fb.group({
    items: this.fb.array([]),
    connected: [[]],
  })

  addressForm = this.fb.group({
    address: ['', [Validators.minLength(2), Validators.maxLength(140), notAllSpacesValidator]],
    city: ['', [Validators.minLength(2), Validators.maxLength(50), notAllSpacesValidator]],
    state: ['', [Validators.minLength(2), Validators.maxLength(50), notAllSpacesValidator]],
    zip: ['', [Validators.minLength(2), Validators.maxLength(50), notAllSpacesValidator]],
    country: [],
    country_code: [],
  })

  settingsForm = createMultiUnitSettingsForm(this.fb)

  form = this.fb.group({
    name: [
      '',
      [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(140),
        notAllNumbersValidator,
        notAllSpacesValidator,
      ],
      RentalNameNotTakenValidator.createValidator(this.store),
    ],
    phone: ['', [notAllSpacesValidator]],
    email: ['', [Validators.required, Validators.minLength(3)]],
    color: [defaultRentalColor],
    address: this.addressForm,
    registration: [],
    gps: [{}],

    unit: this.unitForm,
    settings: this.settingsForm,
  })

  get newUnitsArray() {
    return this.unitForm.get('items') as UntypedFormArray
  }

  get gpsCtrl() {
    return this.form.get('gps') as UntypedFormControl
  }

  subDomain: string
  isStandaloneTokeetSubscription = false
  isLoading = false

  linkedRentals: Rental[] = []
  rentals$ = this.store.pipe(select(selectRentalsWithoutGroupItems))
  isSubunitSelectable = (rental: Rental) => {
    return !this.linkedRentals.find((r) => r.id === rental.id)
  }

  getTooltip = (rental: Rental) => {
    if (this.linkedRentals.find((r) => r.id === rental.id)) {
      return $localize`This rental is connected to an API channel and cannot be selected as subunit.`
    }
    return null
  }

  constructor(
    protected fb: UntypedFormBuilder,
    protected store: Store<any>,
    private actions: Actions,
    private router: Router,
    private toaster: Toaster,
    private storage: UserStorage,
    private confirm: ConfirmDialogService,
    private dataCheckerService: DataCheckerService,
    public dialogRef: MatDialogRef<AddMultiUnitDialogComponent>,
    private amplitudeService: AmplitudeService,
    private successDialog: RentalGroupSuccessDialogService
  ) {
    super()
    this.dataCheckerService.check([SubdomainGuard, AccountGuard])
  }

  ngOnInit() {
    this.onNewUnit()
    this.store.pipe(select(selectActiveTokeetPlanView), untilDestroy(this)).subscribe((sub) => {
      this.isStandaloneTokeetSubscription = sub ? isTokeetStandaloneVersion(sub.version) : false
    })

    this.store.pipe(select(selectSubdomain), untilDestroy(this)).subscribe((subDomain) => {
      this.subDomain = subDomain
    })

    this.store.pipe(select(selectLinkedRentals), untilDestroy(this)).subscribe((rentals) => {
      this.linkedRentals = rentals
    })

    this.actions
      .pipe(
        ofType(addRentalComplete),
        tap((action) => this.newSubunits(action.rental)),
        switchMap((action) => {
          const { items, connected } = this.unitForm.getRawValue()
          const actions: any[] = []
          if (items.length) {
            actions.push(addSubUnitRentalsComplete)
          }
          if (connected.length) {
            actions.push(setRentalSubUnitsComplete)
          }
          return this.actions.pipe(
            ofType(...actions),
            take(1),
            switchMap(() => this.showSuccessDialog(action.rental))
          )
        }),
        untilDestroy(this)
      )
      .subscribe(() => {
        this.close()
      })
  }

  goToAccountSettings() {
    this.router.navigate(['/account'])
    this.close()
  }

  goToConnections() {
    this.router.navigate(['/channels'])
    this.close()
  }

  onNameChange = (name: string) => {
    this.form.patchValue({ email: rentalEmailPrefixFromName(name) })
  }

  onPlaceChange(parsedPlace: ParsedGooglePlace) {
    const country = R.find((c) => c.id === parsedPlace.countryShort, this.countryList)
    this.addressForm.patchValue({
      address: (parsedPlace.streetNumber ? parsedPlace.streetNumber + ' ' : '') + (parsedPlace.street || ''),
      city: parsedPlace.city,
      state: parsedPlace.state,
      country: country?.name,
      country_code: country?.id,
      zip: parsedPlace.postCode,
    })
    this.gpsCtrl.patchValue(parsedPlace.gps)
  }

  onCityChange(parsedPlace: ParsedGooglePlace) {
    const { city } = this.addressForm.getRawValue()

    const googleCity = (parsedPlace.city || '')?.trim()
    this.addressForm.patchValue({
      city: googleCity ? googleCity : city,
    })

    const country = R.find((c: any) => c.id === parsedPlace.countryShort, this.countryList)
    if (country) {
      this.addressForm.patchValue({
        country: country.name,
        country_code: country.id,
      })
    }

    this.gpsCtrl.patchValue(parsedPlace.gps)
  }

  close() {
    this.dialogRef.close()
  }

  onNewUnit() {
    this.newUnitsArray.push(
      this.fb.group({
        name: [
          '',
          [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(140),
            notAllNumbersValidator,
            notAllSpacesValidator,
          ],
          RentalNameNotTakenValidator.createValidator(this.store),
        ],
        multi_unit_priority: [1, [Validators.required, Validators.min(1), integer()]],
        multi_unit_status: [1],
      })
    )
  }

  onRemoveUnit(index: number) {
    this.newUnitsArray.removeAt(index)
  }

  showSuccessDialog(rental: Rental) {
    return this.successDialog
      .open({
        title: $localize`New Multi-Unit Created!`,
        message: $localize`Your Multi-Unit rental has been successfully created.`,
        image: '/assets/images/rental-group/multi-unit-done-1.svg',
        steps: [
          $localize`You can now add and manage subunits inside the rental details overlay under the "Multi-Unit Settings" tab.`,
          $localize`Reservations will be auto-assigned to subunits based on priority.`,
          $localize`Update subunit details, pricing, and availability as needed.`,
        ],
        buttons: [
          {
            label: $localize`Multi-Unit Settings`,
            icon: 'cs-icon cs-icon-door-cog',
            classes: 'btn btn-secondary-info',
            action: () => {
              this.store.dispatch(
                OpenRentalOverlay({ rentalId: rental.id, activeTab: RentalDialogTab.MultiUnitSettings })
              )
            },
          },
          {
            label: $localize`Ok, got it`,
            icon: 'cs-icon cs-icon-check text-xs',
            classes: 'btn btn-secondary-info',
          },
        ],
      })
      .afterClosed()
  }

  showPricingWarning() {
    const disableNotifyKey = 'disable-create-rental-cost-notification'
    if (this.isStandaloneTokeetSubscription && !this.storage.get(disableNotifyKey)) {
      return this.confirm
        .confirmExtra(
          {
            title: $localize`Create Rental`,
            body: $localize`Creating a Rental will increase your pricing. Consult the billing info page for more details on cost.`,
            cancelText: $localize`MOVE TO BILLING INFO`,
            cancelBtnClass: 'btn btn-secondary-info',
            confirmText: $localize`CREATE RENTAL`,
            extra: $localize`Disable this notification forever`,
            defaultExtraValue: false,
          },
          true
        )
        .pipe(
          tap(({ isChecked }) => {
            this.storage.set(disableNotifyKey, isChecked)
          }),
          map(({ status }) => status)
        )
    }
    return of(ConfirmDialogStatus.Confirmed)
  }

  @SaveForm()
  onNext(form: UntypedFormGroup, stepper: MatStepper) {
    const { items, connected } = this.unitForm.getRawValue()
    if (!items?.length && !connected?.length) {
      this.toaster.warning($localize`At least one subunit is required.`)
      return
    }
    this.form
      .get('email')
      .addAsyncValidators([
        RentalEmailNotTakenValidator.createValidator(this.store),
        RentalEmailValidator.createValidator(this.store),
      ])
    this.form.get('email').updateValueAndValidity()
    stepper.next()
  }

  onPrevious(stepper: MatStepper) {
    this.form.get('email').clearAsyncValidators()
    this.form.get('email').updateValueAndValidity()
    stepper.previous()
  }

  newSubunits(mainRental: Rental) {
    const { items, connected } = this.unitForm.getRawValue()
    const { unit, ...data } = this.form.getRawValue()
    const newItems = lodash.map(
      items,
      (item: { name: string; multi_unit_priority: number; multi_unit_status: number }, index): CreateRentalPayload => {
        return {
          ...data,
          ...item,
          ...createRentalPayloadFromMainUnit(mainRental, item.name, this.subDomain),
        }
      }
    )
    if (newItems.length) {
      this.store.dispatch(addSubUnitRentals({ items: newItems }))
    }
    if (connected.length) {
      this.store.dispatch(
        setRentalSubUnits({
          rentalId: mainRental.id,
          addedChildren: [...connected, mainRental.id],
          removedChildren: [],
          silent: true,
        })
      )
    }
  }

  @SaveForm()
  onSave(form: UntypedFormGroup) {
    const { items, connected } = this.unitForm.getRawValue()
    if (!items?.length && !connected?.length) {
      this.toaster.warning($localize`At least one subunit is required.`)
      return
    }

    const { unit, settings, ...data } = form.getRawValue()
    const payload: CreateRentalPayload = data
    payload.email = convertToTokeetEmail(payload.email, this.subDomain)
    payload.multi_unit_main = true
    const doAction = () => {
      this.amplitudeService.logEvent('add-rental-manually')
      this.store.dispatch(addRental({ payload: { ...payload, multi_unit_settings: settings } }))
    }

    this.showPricingWarning().subscribe((status) => {
      if (status === ConfirmDialogStatus.Canceled) {
        this.close()
        this.router.navigate(['/billing/info'])
      } else if (status === ConfirmDialogStatus.Confirmed) {
        doAction()
      }
    })
  }
}
